import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { Play, ExternalLink } from 'lucide-react';
const SEAN_NEWS = 'https://cdn.magicpatterns.com/uploads/eP8xBRmufMLXR2LKa3kBny/2023-03-19_(1).jpg';
const TEAM_OFFICE = 'https://cdn.magicpatterns.com/uploads/8ACCo3tEEUuM4jPTQJ488s/2023-03-19.jpg';
const OLD_HOMEPAGE = 'https://cdn.magicpatterns.com/uploads/sXM52GgeNkPCu3uWJWrdKY/blah.gif';
const REPORT_CENTER = 'https://cdn.magicpatterns.com/uploads/dBFEvmAapYVvmkEpxZheQU/report_center_with_menu.jpg';
const SEVERITY = 'https://cdn.magicpatterns.com/uploads/eQwhLyWkjbufFFxEGgNa6b/vulnerability_severity_levels_full.jpg';
const LOGO_DOOR = 'https://cdn.magicpatterns.com/uploads/hiJai4optHg9SkvWFwmCsN/IMG_0424.jpg';
const timeline = [{
  year: '2004',
  title: 'Periscan founded',
  body: 'Sean Heiney founds Periscan on a thesis ahead of its time: security assessment should be automated, continuous, and outside-in.'
}, {
  year: '2005',
  title: '"Hacker\'s-eye view"',
  body: "Periscan describes its service as managed automated network security — providing a constant hacker's-eye view of customer networks. Before the industry had names for it."
}, {
  year: '2006',
  title: '"The Hacker On Your Side" hits the news',
  body: 'Local news covers Periscan as the first continuous autonomous hacker on your side — a hosted service constantly walking customer networks the way an attacker would. Twenty years before "AEV" was a Gartner acronym.'
}, {
  year: 'Acquired',
  title: 'Catbird Networks',
  body: 'The original Periscan is acquired by Catbird Networks. The brand eventually goes quiet — but the thesis stays alive.'
}, {
  year: '2026',
  title: 'Periscan reborn',
  body: 'The original founder brings Periscan back, rebuilt for cloud, identity, SaaS, code, and AI applications. The market finally has a name — and a budget — for what we did first.',
  active: true
}];
export function History() {
  return <section id="history" className="relative w-full py-32 border-t border-white/5 overflow-hidden" data-id="element-772">
      {/* Massive faded year type in background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-773">
        <div className="font-mono text-[18vw] font-bold text-white/[0.018] tracking-tighter leading-none select-none" data-id="element-774">
          2004→2026
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8" data-id="element-775">
        <SectionLabel number="04" label="History" className="mb-8" data-id="element-776" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-777">
          <div className="lg:col-span-8" data-id="element-778">
            <SectionHeading size="xl" data-id="element-779">
              We invented continuous validation
              <br data-id="element-780" />
              <span className="text-white/40" data-id="element-781">
                before the category had a name.
              </span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-4 lg:pt-4" data-id="element-782">
            <p className="text-base text-white/60 leading-relaxed" data-id="element-783">
              Founded in 2004. Acquired. Quiet. Reborn in 2026. Same founder.
              Same thesis. Much bigger surface.
            </p>
          </div>
        </div>

        {/* ── Hero artifact: the office door logo ───────────────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#070a0e] mb-24" data-id="element-784">
          <div className="relative aspect-[21/9]" data-id="element-785">
            <img src={LOGO_DOOR} alt="The original Periscan logo on the office door" className="absolute inset-0 w-full h-full object-cover" data-id="element-786" />
            {/* Cinematic gradient overlays */}
            <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(90deg, rgba(5,6,8,0.85) 0%, rgba(5,6,8,0.4) 35%, transparent 60%)'
          }} data-id="element-787" />
            <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(180deg, transparent 60%, rgba(5,6,8,0.6) 100%)'
          }} data-id="element-788" />

            {/* Floating caption */}
            <div className="absolute inset-0 flex items-center" data-id="element-789">
              <div className="px-8 md:px-16 max-w-xl" data-id="element-790">
                <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-791">
                  Original office · ~2005
                </p>
                <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.1] mb-4" data-id="element-792">
                  Behind that door,
                  <br data-id="element-793" />
                  <span className="text-white/50" data-id="element-794">
                    a thesis 20 years early.
                  </span>
                </h3>
                <p className="text-sm md:text-base text-white/70 leading-relaxed" data-id="element-795">
                  Continuous, automated, outside-in security validation —
                  hosted, subscription, shipped. Before the market had any of
                  those words.
                </p>
              </div>
            </div>

            {/* Corner label */}
            <div className="absolute top-6 right-6 text-right" data-id="element-796">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80" data-id="element-797">
                Archive · Photo
              </span>
            </div>
          </div>
        </motion.div>

        {/* ── News clip artifact ────────────────────────────────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-24" data-id="element-798">
          <div className="lg:col-span-7 relative rounded-2xl overflow-hidden border border-white/10 bg-[#070a0e] group" data-id="element-799">
            <div className="relative aspect-[4/3] w-full" data-id="element-800">
              <img src={SEAN_NEWS} alt="Sean Heiney, President of Periscan, interviewed on Channel 6 News" className="absolute inset-0 w-full h-full object-cover" data-id="element-801" />
              {/* CRT scanline overlay */}
              <div className="absolute inset-0 mix-blend-overlay opacity-20 pointer-events-none" style={{
              backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 3px)'
            }} data-id="element-802" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2" data-id="element-803">
                <Play className="w-3 h-3 text-[#1a8fd4] fill-[#1a8fd4]" data-id="element-804" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80" data-id="element-805">
                  News Segment · Channel 6
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 px-5 py-3 bg-gradient-to-t from-black/80 to-transparent" data-id="element-806">
                <p className="font-mono text-xs text-white/80 uppercase tracking-widest" data-id="element-807">
                  Sean Heiney · President, Periscan
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-between" data-id="element-808">
            <div data-id="element-809">
              <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-810">
                Press · 2006
              </p>
              <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-4 leading-snug" data-id="element-811">
                Local news covers the first continuous autonomous
                <br data-id="element-812" />
                "hacker on your side."
              </h3>
              <p className="text-base text-white/60 leading-relaxed" data-id="element-813">
                Long before "ASV," "CTEM," or "AEV" were acronyms, Periscan was
                already on local TV explaining why companies needed continuous
                outside-in security validation. We were early. The market
                eventually agreed.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-white/5 flex items-center gap-2 text-xs text-white/40 font-mono uppercase tracking-widest" data-id="element-814">
              <ExternalLink className="w-3 h-3" data-id="element-815" />
              Original company press archive
            </div>
          </div>
        </motion.div>

        {/* ── Original product artifacts ────────────────────────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-24" data-id="element-816">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-10" data-id="element-817">
            <div className="lg:col-span-7" data-id="element-818">
              <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-3" data-id="element-819">
                Original product · circa 2005
              </p>
              <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-3 leading-snug" data-id="element-820">
                "The Hacker on Your Side."
              </h3>
              <p className="text-base text-white/60 leading-relaxed max-w-2xl" data-id="element-821">
                The first Periscan ran constant outside-in scans, generated
                executive and technical reports, and gave customers severity
                ratings against real-world threat scope. Continuous validation,
                shipped as a hosted service — twenty years before the category
                got its name.
              </p>
            </div>
          </div>

          {/* 3-cell composition: hero homepage (2/3) + two stacked supporting shots (1/3) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:auto-rows-fr" data-id="element-822">
            {/* HERO — Periscan.com homepage (large, dominates) */}
            <div className="md:col-span-8 md:row-span-2 relative rounded-xl overflow-hidden border border-white/10 bg-[#070a0e] flex flex-col" data-id="element-823">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0a0d12] border-b border-white/[0.06]" data-id="element-824">
                <div className="flex items-center gap-1.5" data-id="element-825">
                  <span className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-826" />
                  <span className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-827" />
                  <span className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-828" />
                </div>
                <div className="flex-1 ml-3 px-3 py-1 rounded-md bg-black/40 border border-white/5" data-id="element-829">
                  <span className="font-mono text-[10px] text-white/50" data-id="element-830">
                    periscan.com
                  </span>
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/30 hidden sm:inline" data-id="element-831">
                  Archive · ~2005
                </span>
              </div>
              <div className="relative flex-1 bg-black min-h-[320px] md:min-h-[480px]" data-id="element-832">
                <img src={OLD_HOMEPAGE} alt="Original Periscan homepage — The Hacker on Your Side" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-833" />
                <div className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(180deg, transparent 75%, rgba(5,6,8,0.7) 100%)'
              }} data-id="element-834" />
                <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-4" data-id="element-835">
                  <p className="font-mono text-[11px] md:text-xs uppercase tracking-widest text-white/80" data-id="element-836">
                    "The Hacker on Your Side."
                  </p>
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/70" data-id="element-837">
                    Original homepage
                  </span>
                </div>
              </div>
            </div>

            {/* Supporting tile 1 — Report Center */}
            <div className="md:col-span-4 relative rounded-xl overflow-hidden border border-white/10 bg-[#070a0e] flex flex-col" data-id="element-838">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#0a0d12] border-b border-white/[0.06]" data-id="element-839">
                <div className="flex items-center gap-1.5" data-id="element-840">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/15" data-id="element-841" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/15" data-id="element-842" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/15" data-id="element-843" />
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/60 ml-1.5" data-id="element-844">
                  Report Center
                </span>
              </div>
              <div className="relative flex-1 bg-white min-h-[200px]" data-id="element-845">
                <img src={REPORT_CENTER} alt="Original Periscan Report Center showing scheduled and recent compliance reports" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-846" />
              </div>
            </div>

            {/* Supporting tile 2 — Vulnerability Severity */}
            <div className="md:col-span-4 relative rounded-xl overflow-hidden border border-white/10 bg-[#070a0e] flex flex-col" data-id="element-847">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#0a0d12] border-b border-white/[0.06]" data-id="element-848">
                <div className="flex items-center gap-1.5" data-id="element-849">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/15" data-id="element-850" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/15" data-id="element-851" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/15" data-id="element-852" />
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/60 ml-1.5" data-id="element-853">
                  Severity
                </span>
              </div>
              <div className="relative flex-1 bg-white min-h-[200px]" data-id="element-854">
                <img src={SEVERITY} alt="Original Periscan Vulnerability Severity Levels reference page" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-855" />
              </div>
            </div>
          </div>

          {/* Caption strip */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/40 font-mono uppercase tracking-widest" data-id="element-856">
            <span className="inline-flex items-center gap-2" data-id="element-857">
              <span className="w-1 h-1 rounded-full bg-[#1a8fd4]" data-id="element-858" />
              Hosted SaaS
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-859">
              <span className="w-1 h-1 rounded-full bg-[#1a8fd4]" data-id="element-860" />
              Continuous scanning
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-861">
              <span className="w-1 h-1 rounded-full bg-[#1a8fd4]" data-id="element-862" />
              Executive + technical reports
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-863">
              <span className="w-1 h-1 rounded-full bg-[#1a8fd4]" data-id="element-864" />
              Severity ratings
            </span>
          </div>
        </motion.div>

        {/* ── Team artifact ─────────────────────────────────────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-24" data-id="element-865">
          <div className="lg:col-span-5 lg:order-2 flex flex-col justify-center" data-id="element-866">
            <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-3" data-id="element-867">
              The original team
            </p>
            <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-3 leading-snug" data-id="element-868">
              Pizza, coffee, late nights.
            </h3>
            <p className="text-base text-white/60 leading-relaxed" data-id="element-869">
              Small team. Big thesis. Same energy — different decade.
            </p>
          </div>
          <div className="lg:col-span-7 lg:order-1 relative rounded-2xl overflow-hidden border border-white/10 bg-[#070a0e]" data-id="element-870">
            <img src={TEAM_OFFICE} alt="The original Periscan team in their office" className="w-full h-auto object-cover" data-id="element-871" />
            <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(180deg, transparent 60%, rgba(5,6,8,0.4) 100%)'
          }} data-id="element-872" />
          </div>
        </motion.div>

        {/* ── Two-track market timeline ────────────────────────────────── */}
        <div className="relative" data-id="element-873">
          {/* Section header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-874">
            <div className="lg:col-span-8" data-id="element-875">
              <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-876">
                Before the acronyms
              </p>
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.1] mb-6" data-id="element-877">
                The market spent two decades
                <br data-id="element-878" />
                <span className="text-white/40" data-id="element-879">
                  inventing acronyms for what
                  <br data-id="element-880" />
                  Periscan started doing in 2004.
                </span>
              </h3>
            </div>
            <div className="lg:col-span-4 lg:pt-4" data-id="element-881">
              <p className="text-base text-white/60 leading-relaxed" data-id="element-882">
                BAS, AEV, ASV, CTEM, and EASM all name parts of the same shift:
                security testing becoming continuous, automated, and
                evidence-backed.
              </p>
            </div>
          </div>

          {/* Acronym sweep */}
          <div className="flex flex-wrap items-center gap-2 mb-16 p-5 rounded-2xl bg-[#070a0e] border border-white/[0.06]" data-id="element-883">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 mr-2" data-id="element-884">
              Acronyms invented after 2004:
            </span>
            {['BAS', 'PTaaS', 'ASM', 'EASM', 'CTEM', 'AEV', 'ASV'].map(a => <span key={a} className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-white/70 bg-white/[0.04] border border-white/10 rounded-md" data-id="element-885">
                {a}
              </span>)}
          </div>

          {/* Track labels */}
          <div className="hidden md:grid grid-cols-12 gap-6 mb-6" data-id="element-886">
            <div className="col-span-5 text-right" data-id="element-887">
              <div className="inline-flex items-center gap-2" data-id="element-888">
                <span className="w-2 h-2 rounded-full bg-[#1a8fd4] shadow-[0_0_8px_#1a8fd4]" data-id="element-889" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4]" data-id="element-890">
                  Periscan thread
                </span>
              </div>
            </div>
            <div className="col-span-2" data-id="element-891" />
            <div className="col-span-5 text-left" data-id="element-892">
              <div className="inline-flex items-center gap-2" data-id="element-893">
                <span className="w-2 h-2 rounded-full bg-white/30" data-id="element-894" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50" data-id="element-895">
                  Market thread
                </span>
              </div>
            </div>
          </div>

          {/* Spine */}
          <div className="relative" data-id="element-896">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#1a8fd4]/40 via-white/10 to-[#1a8fd4]/40 hidden md:block" data-id="element-897" />

            {/* 2004 — Periscan founded (hero anchor) */}
            <TimelineEntry year="2004" side="periscan" hero title="Periscan is founded." body="Sean Heiney founds Periscan around the idea that security assessment should not be manual, occasional, or point-in-time. It should be automated. It should be continuous. It should give defenders the attacker's view." chips={['Founded', 'Continuous validation', 'Outside-in']} data-id="element-898" />

            {/* 2005 — Hacker's-eye view */}
            <TimelineEntry year="2005" side="periscan" title={'"A hacker\'s-eye view of the network."'} body="The original Periscan describes its service as working constantly to provide a hacker's-eye view of the customer's network. Before ASV. Before AEV. Before CTEM. Before BAS. Before EASM. Periscan was already doing the thing." chips={['Hacker\u2019s-eye view', 'Hosted SaaS', 'PCI scope']} data-id="element-899" />

            {/* 2012 — HackerOne */}
            <TimelineEntry year="2012" side="market" title="Human-powered validation scales." body="HackerOne is founded, helping popularize bug bounty and hacker-powered security as a scalable way to find real vulnerabilities with human researchers. The market starts accepting that security testing can be ongoing, externalized, and platform-mediated." vendors={['HackerOne']} chips={['Bug bounty', 'Crowdsourced']} data-id="element-900" />

            {/* 2013–2014 — The acronyms begin */}
            <TimelineEntry year="2013–2014" side="market" title="The acronyms begin." body="Years after Periscan was already doing continuous validation, the market started giving pieces of the idea their own names. The first big slice: Breach and Attack Simulation. Test the controls. See what fires. Find what misses. Useful? Absolutely. The whole story? Not even close. Periscan's original thesis was broader: continuously understand exposure, validate what matters, alert before attackers act, and prove the environment is getting safer." vendors={['Picus', 'AttackIQ', 'SafeBreach']} chips={['BAS', 'First slice of the idea']} acronym="BAS" data-id="element-901" />

            {/* 2015 — Pcysys / Pentera */}
            <TimelineEntry year="2015" side="market" title="Automated security validation takes shape." body="Pcysys, later renamed Pentera, is founded — defining the exposure validation market and helping automate the attacker's path against live environments." vendors={['Pcysys / Pentera']} chips={['Automated pentest', 'Exposure validation']} data-id="element-902" />

            {/* 2016 — Cymulate, XM Cyber */}
            <TimelineEntry year="2016" side="market" title="Exposure and attack-path platforms accelerate." body="Cymulate is founded and becomes part of the continuous validation / exposure validation wave. XM Cyber is also founded, building around attack-path simulation and exposure management. The market moves closer to Periscan's original idea: don't just scan — validate what the attacker can actually do." vendors={['Cymulate', 'XM Cyber']} chips={['Attack-path', 'Exposure management']} data-id="element-903" />

            {/* 2017–2019 — ASM + autonomous testing */}
            <TimelineEntry year="2017–2019" side="market" title="Attack surface and autonomous testing mature." body="CyCognito launches around the attacker's view of external attack surface risk. Horizon3.ai brings continuous autonomous penetration testing. Ridge Security, FireCompass, and BreachLock also emerge in the offensive testing / PTaaS / continuous validation wave. Different labels. Same pressure: security changes too fast for point-in-time testing." vendors={['CyCognito', 'Horizon3.ai', 'Ridge Security', 'FireCompass', 'BreachLock']} chips={['ASM', 'PTaaS', 'Continuous red teaming']} data-id="element-904" />

            {/* 2021 — EASM named */}
            <TimelineEntry year="2021" side="market" title="EASM becomes a named category." body="Gartner recognizes External Attack Surface Management as an emerging vendor category. The market formally names another piece of Periscan's old idea: know what attackers can see from the outside." chips={['EASM']} acronym="EASM" data-id="element-905" />

            {/* 2022 — CTEM */}
            <TimelineEntry year="2022" side="market" title="CTEM enters the conversation." body="Continuous Threat Exposure Management becomes the operating model for moving beyond periodic vulnerability management — and toward continuous scoping, discovery, prioritization, validation, and mobilization. The industry finally starts treating exposure reduction as a continuous loop." chips={['CTEM', 'Continuous loop']} acronym="CTEM" data-id="element-906" />

            {/* 2025–2026 — AEV + ASV consolidation */}
            <TimelineEntry year="2025–2026" side="market" title="AEV and ASV consolidate the market." body="Gartner frames Adversarial Exposure Validation as a category that replaces earlier BAS, automated pentesting, and red-team automation. Frost & Sullivan frames Automated Security Validation as a convergence of BAS, autonomous pentesting, red teaming automation, and continuous exposure management around one outcome: continuously validating security effectiveness in real-world situations." chips={['AEV', 'ASV', 'Convergence']} acronym="AEV · ASV" data-id="element-907" />

            {/* 2026 — Periscan returns (hero finale) */}
            <TimelineEntry year="2026" side="periscan" hero active title="Periscan returns." body="The old Periscan gave companies a hacker's-eye view of their network. The new Periscan validates exposure, controls, attack paths, AI applications, and fixes — then turns the results into proof. The market that we anticipated finally caught up." chips={['Exposure validation', 'Control validation', 'AI app validation', 'Evidence-driven']} tagline="Find the path. Validate the risk. Prove it's fixed." data-id="element-908" />
          </div>
        </div>

        {/* ── Closing callout ───────────────────────────────────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-24 p-8 md:p-14 rounded-2xl bg-gradient-to-br from-[#1a8fd4]/10 to-transparent border border-[#1a8fd4]/20" data-id="element-909">
          <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-910">
            The category finally has a name
          </p>
          <p className="text-3xl md:text-4xl text-white leading-snug tracking-tight max-w-4xl mb-4" data-id="element-911">
            Different acronyms.{' '}
            <span className="text-white/60" data-id="element-912">Same direction.</span>
          </p>
          <p className="text-lg text-white/70 leading-relaxed max-w-3xl mb-6" data-id="element-913">
            Security moved from point-in-time testing to continuous proof.
            Periscan was early. Now it's back.
          </p>
          <p className="font-mono text-base text-[#1a8fd4]" data-id="element-914">
            Find the path. Validate the risk. Prove it's fixed.
          </p>
        </motion.div>
      </div>
    </section>;
}
// ── Timeline entry component ────────────────────────────────────────────
type TimelineEntryProps = {
  year: string;
  side: 'periscan' | 'market';
  title: string;
  body: string;
  hero?: boolean;
  active?: boolean;
  vendors?: string[];
  chips?: string[];
  acronym?: string;
  tagline?: string;
};
function TimelineEntry({
  year,
  side,
  title,
  body,
  hero,
  active,
  vendors,
  chips,
  acronym,
  tagline
}: TimelineEntryProps) {
  const isPeriscan = side === 'periscan';
  // Hero entries take full width
  if (hero) {
    return <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-50px'
    }} transition={{
      duration: 0.5
    }} className="relative my-6 md:my-8" data-id="element-915">
        {/* Center node */}
        <div className="hidden md:block absolute left-1/2 -translate-x-1/2 -top-1 z-10" data-id="element-916">
          <div className={`relative w-4 h-4 rounded-full ${active ? 'bg-[#1a8fd4] shadow-[0_0_16px_#1a8fd4]' : 'bg-[#1a8fd4]/40 border border-[#1a8fd4]/60'}`} data-id="element-917" />
        </div>

        <div className={`relative mt-6 p-8 md:p-10 rounded-2xl border ${active ? 'bg-gradient-to-br from-[#1a8fd4]/12 to-[#0a0d12] border-[#1a8fd4]/40' : 'bg-gradient-to-br from-[#1a8fd4]/8 to-[#0a0d12] border-[#1a8fd4]/25'}`} data-id="element-918">
          <div className="flex flex-wrap items-baseline gap-4 mb-4" data-id="element-919">
            <span className={`font-mono text-3xl md:text-4xl font-bold ${active ? 'text-[#1a8fd4]' : 'text-white'}`} data-id="element-920">
              {year}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4]" data-id="element-921">
              Periscan
            </span>
          </div>
          <h4 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-snug mb-4 max-w-3xl" data-id="element-922">
            {title}
          </h4>
          <p className="text-base text-white/70 leading-relaxed max-w-3xl mb-5" data-id="element-923">
            {body}
          </p>
          {chips && chips.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-924">
              {chips.map(c => <span key={c} className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-[#1a8fd4] bg-[#1a8fd4]/10 border border-[#1a8fd4]/25 rounded-md" data-id="element-925">
                  {c}
                </span>)}
            </div>}
          {tagline && <p className="font-mono text-sm text-[#1a8fd4] mt-4 pt-4 border-t border-white/5" data-id="element-926">
              {tagline}
            </p>}
        </div>
      </motion.div>;
  }
  // Standard side-aligned entries
  return <motion.div initial={{
    opacity: 0,
    x: isPeriscan ? -16 : 16
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.45
  }} className="relative grid grid-cols-1 md:grid-cols-12 gap-6 my-2 md:my-3" data-id="element-927">
      {/* Center node */}
      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-6 z-10" data-id="element-928">
        <div className={`w-3 h-3 rounded-full ${isPeriscan ? 'bg-[#1a8fd4] shadow-[0_0_10px_#1a8fd4]' : 'bg-white/25 border border-white/10'}`} data-id="element-929" />
      </div>

      {/* Periscan side — left column */}
      {isPeriscan ? <>
          <div className="md:col-span-5 md:text-right" data-id="element-930">
            <div className="inline-block max-w-md p-6 rounded-xl bg-[#0a0d12] border border-[#1a8fd4]/25 text-left" data-id="element-931">
              <div className="flex items-baseline gap-3 mb-3 md:justify-end md:flex-row-reverse" data-id="element-932">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4]" data-id="element-933">
                  Periscan
                </span>
                <span className="font-mono text-lg font-bold text-white" data-id="element-934">
                  {year}
                </span>
              </div>
              <h4 className="text-base font-semibold text-white tracking-tight leading-snug mb-2" data-id="element-935">
                {title}
              </h4>
              <p className="text-sm text-white/65 leading-relaxed" data-id="element-936">{body}</p>
              {chips && chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-4" data-id="element-937">
                  {chips.map(c => <span key={c} className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-[#1a8fd4] bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 rounded" data-id="element-938">
                      {c}
                    </span>)}
                </div>}
            </div>
          </div>
          <div className="hidden md:block md:col-span-7" data-id="element-939" />
        </> : <>
          <div className="hidden md:block md:col-span-5" data-id="element-940" />
          <div className="md:col-span-7 md:col-start-6" data-id="element-941">
            <div className="max-w-xl p-6 rounded-xl bg-[#070a0e] border border-white/[0.06] hover:border-white/15 transition-colors" data-id="element-942">
              <div className="flex flex-wrap items-baseline gap-3 mb-3" data-id="element-943">
                <span className="font-mono text-lg font-bold text-white/85" data-id="element-944">
                  {year}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-id="element-945">
                  Market
                </span>
                {acronym && <span className="ml-auto px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-white/60 bg-white/[0.04] border border-white/10 rounded" data-id="element-946">
                    {acronym}
                  </span>}
              </div>
              <h4 className="text-base font-semibold text-white/90 tracking-tight leading-snug mb-2" data-id="element-947">
                {title}
              </h4>
              <p className="text-sm text-white/55 leading-relaxed" data-id="element-948">{body}</p>

              {vendors && vendors.length > 0 && <div className="mt-4 pt-3 border-t border-white/5" data-id="element-949">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/35 mb-2" data-id="element-950">
                    Vendors
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-951">
                    {vendors.map(v => <span key={v} className="px-2 py-0.5 text-[10px] text-white/65 bg-white/[0.03] border border-white/10 rounded" data-id="element-952">
                        {v}
                      </span>)}
                  </div>
                </div>}

              {chips && chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3" data-id="element-953">
                  {chips.map(c => <span key={c} className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-white/45 bg-white/[0.02] border border-white/5 rounded" data-id="element-954">
                      {c}
                    </span>)}
                </div>}
            </div>
          </div>
        </>}
    </motion.div>;
}